import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Layout } from "../components/Layout";
import { api, API } from "../lib/api";
import OwnerPicker from "../components/OwnerPicker";
import { Loader2, ArrowLeft, Download, RefreshCw, Save, Send, CheckCircle2, Globe } from "lucide-react";

export default function AdminSubmission() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [doc, setDoc] = useState(null);
    const [draft, setDraft] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [regen, setRegen] = useState(false);
    const [sending, setSending] = useState(false);
    const [auditing, setAuditing] = useState(false);
    const [err, setErr] = useState("");
    const [msg, setMsg] = useState("");

    const load = async () => {
        try {
            const { data } = await api.get(`/admin/submissions/${id}`);
            setDoc(data);
            setDraft(data.report_draft || {});
        } catch (e) {
            if (e.response?.status === 401) {
                localStorage.removeItem("ash_admin_token");
                navigate("/admin/login");
                return;
            }
            setErr(e.response?.data?.detail || "Failed");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const save = async () => {
        setSaving(true);
        setMsg("");
        try {
            await api.patch(`/admin/submissions/${id}/report`, draft);
            setMsg("Saved");
            setTimeout(() => setMsg(""), 1500);
        } catch (e) {
            setErr(e.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const regenerate = async () => {
        setRegen(true);
        try {
            const { data } = await api.post(`/admin/submissions/${id}/regenerate`);
            setDraft(data);
            setMsg("Regenerated");
            setTimeout(() => setMsg(""), 1500);
        } catch (e) {
            setErr(e.response?.data?.detail || "Regenerate failed");
        } finally {
            setRegen(false);
        }
    };

    const downloadPdf = async () => {
        const token = localStorage.getItem("ash_admin_token");
        const res = await fetch(`${API}/admin/submissions/${id}/pdf`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
            setErr("Could not download PDF");
            return;
        }
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `ASH-Clarity-Report-${doc.organization.replace(/\s+/g, "_")}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    const runAudit = async () => {
        setAuditing(true);
        setErr("");
        setMsg("");
        try {
            await api.post(`/admin/submissions/${id}/run-audit`);
            setMsg("Website audit started — refresh in ~30-60 seconds to see results.");
            setTimeout(() => setMsg(""), 6000);
        } catch (e) {
            setErr(e.response?.data?.detail || "Could not start audit");
        } finally {
            setAuditing(false);
        }
    };

    const sendPdf = async () => {
        const alreadySent = !!doc.pdf_sent_at;
        const confirmMsg = alreadySent
            ? `This report was already sent on ${new Date(doc.pdf_sent_at).toLocaleString()}. Send it again to ${doc.email}?`
            : `Send the current PDF to ${doc.email}? A copy will be CC'd to your team inbox.`;
        if (!window.confirm(confirmMsg)) return;
        setSending(true);
        setErr("");
        setMsg("");
        try {
            await api.post(`/admin/submissions/${id}/send-pdf`);
            await load();
            setMsg("PDF sent");
            setTimeout(() => setMsg(""), 2500);
        } catch (e) {
            setErr(e.response?.data?.detail || "Could not send PDF");
        } finally {
            setSending(false);
        }
    };

    if (loading) {
        return (
            <Layout minimal>
                <div className="max-w-3xl mx-auto px-6 py-20 flex items-center gap-2 text-ash-gray-dark">
                    <Loader2 className="animate-spin" /> Loading…
                </div>
            </Layout>
        );
    }
    if (err || !doc) {
        return (
            <Layout minimal>
                <div className="max-w-3xl mx-auto px-6 py-20" data-testid="admin-sub-error">
                    <div className="text-red-600">{err}</div>
                    <Link to="/admin" className="btn-outline mt-4 inline-flex">Back</Link>
                </div>
            </Layout>
        );
    }

    const setF = (k) => (e) => setDraft({ ...draft, [k]: e.target.value });
    const setNote = (k) => (e) =>
        setDraft({ ...draft, category_notes: { ...(draft.category_notes || {}), [k]: e.target.value } });
    const setListItem = (key, idx) => (e) => {
        const list = [...(draft[key] || [])];
        list[idx] = e.target.value;
        setDraft({ ...draft, [key]: list });
    };

    const labels = doc.category_labels || {};
    const hasOwner = !!(doc.owner && doc.owner.trim());

    return (
        <Layout minimal>
            <section className="max-w-5xl mx-auto px-6 md:px-10 py-10" data-testid="admin-submission-section">
                <Link to="/admin" className="text-ash-gray-dark text-sm flex items-center gap-1 mb-4" data-testid="back-to-admin">
                    <ArrowLeft size={14} /> Back
                </Link>

                <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
                    <div>
                        <div className="text-xs uppercase tracking-widest font-bold text-ash-orange mb-2">Submission</div>
                        <h1 className="font-display font-black text-4xl tracking-tighter text-ash-black">{doc.organization}</h1>
                        <p className="text-ash-gray-dark mt-1">{doc.first_name} {doc.last_name} • {doc.email}</p>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        <button onClick={regenerate} disabled={regen} className="btn-outline flex items-center gap-2" data-testid="regenerate-btn">
                            {regen ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
                            Regenerate AI draft
                        </button>
                        <button onClick={downloadPdf} className="btn-outline flex items-center gap-2" data-testid="download-pdf-btn">
                            <Download size={16} /> Download PDF
                        </button>
                        <button
                            onClick={sendPdf}
                            disabled={sending || !hasOwner}
                            className="btn-primary flex items-center gap-2"
                            data-testid="send-pdf-btn"
                            title={!hasOwner ? "Assign an owner before sending the report" : ""}
                        >
                            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                            {doc.pdf_sent_at ? "Resend PDF to user" : "Send PDF to user"}
                        </button>
                    </div>
                </div>

                {/* Owner + Report Sent meta bar */}
                <div className="bg-white border border-ash-gray-light p-4 mb-6 flex flex-wrap items-center gap-6" data-testid="submission-meta-bar">
                    <div>
                        <div className="text-xs uppercase tracking-widest font-bold text-ash-gray-dark mb-2">Owner</div>
                        <OwnerPicker
                            submissionId={doc.id}
                            value={doc.owner || ""}
                            onSaved={(v) => setDoc({ ...doc, owner: v })}
                        />
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-widest font-bold text-ash-gray-dark mb-2">Report sent</div>
                        <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="meta-report-sent-label">
                            <input
                                type="checkbox"
                                className="w-5 h-5 accent-ash-orange"
                                checked={!!doc.report_sent}
                                onChange={async (e) => {
                                    const next = e.target.checked;
                                    setDoc({ ...doc, report_sent: next });
                                    try {
                                        await api.patch(`/admin/submissions/${doc.id}/meta`, { report_sent: next });
                                    } catch (er) {
                                        setDoc({ ...doc, report_sent: !next });
                                        setErr(er.response?.data?.detail || "Could not update");
                                    }
                                }}
                                data-testid="meta-report-sent"
                            />
                            <span className="text-sm">Mark as sent</span>
                        </label>
                    </div>
                    {!hasOwner && (
                        <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2" data-testid="owner-required-hint">
                            Assign an owner above to enable <strong>Send PDF to user</strong>.
                        </div>
                    )}
                </div>

                {doc.pdf_sent_at && (
                    <div className="mb-6 inline-flex items-center gap-2 bg-ash-orange-50 border border-ash-orange/30 px-3 py-2 text-sm" data-testid="pdf-sent-status">
                        <CheckCircle2 size={16} className="text-ash-orange" />
                        <span className="text-ash-black">
                            <strong>PDF sent</strong> to {doc.pdf_sent_to || doc.email} on {new Date(doc.pdf_sent_at).toLocaleString()}
                            {doc.pdf_sent_by ? <> by <strong>{doc.pdf_sent_by}</strong></> : null}
                        </span>
                    </div>
                )}

                {err && (
                    <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm p-3" data-testid="admin-sub-error-banner">{err}</div>
                )}

                {/* Website audit (Lighthouse) */}
                {doc.website && (
                    <div className="bg-white border border-ash-gray-light p-5 mb-6" data-testid="website-audit-section">
                        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
                            <div className="flex items-center gap-2">
                                <Globe size={18} className="text-ash-orange" />
                                <h2 className="font-display font-bold text-lg">Website snapshot (Lighthouse)</h2>
                            </div>
                            <button onClick={runAudit} disabled={auditing} className="btn-outline flex items-center gap-2 text-sm" data-testid="run-audit-btn">
                                {auditing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                                {doc.lighthouse ? "Re-run audit" : "Run audit"}
                            </button>
                        </div>
                        <WebsiteAudit lighthouse={doc.lighthouse} url={doc.website} />
                    </div>
                )}

                {/* Top stats */}
                <div className="grid sm:grid-cols-4 gap-4 mb-10">
                    <Stat label="Score" value={`${doc.overall_score}/100`} accent />
                    <Stat label="Profile" value={doc.profile_name} />
                    <Stat label="Lead readiness" value={doc.lead_readiness} />
                    <Stat label="Report requested" value={doc.requested_report ? "Yes" : "No"} />
                </div>

                <div className="grid lg:grid-cols-3 gap-8">
                    {/* Quiz answers */}
                    <div className="lg:col-span-1 bg-white border border-ash-gray-light p-6">
                        <h2 className="font-display font-bold text-xl mb-4">Quiz answers</h2>
                        <dl className="space-y-3 text-sm">
                            {Object.entries(doc.answers || {}).map(([k, v]) => (
                                <div key={k}>
                                    <dt className="text-xs uppercase tracking-widest font-bold text-ash-gray-dark">{k}</dt>
                                    <dd className="text-ash-black">
                                        {Array.isArray(v) ? (
                                            <ul className="list-disc pl-5 space-y-0.5">
                                                {v.map((item, i) => (
                                                    <li key={i}>{String(item)}</li>
                                                ))}
                                            </ul>
                                        ) : v === null || v === undefined || v === "" ? (
                                            <span className="text-ash-gray-dark">—</span>
                                        ) : (
                                            String(v)
                                        )}
                                    </dd>
                                </div>
                            ))}
                        </dl>
                        <h3 className="font-display font-bold mt-6 mb-3">Category scores</h3>
                        <div className="space-y-2">
                            {Object.entries(labels).map(([k, lbl]) => (
                                <div key={k} className="text-sm flex justify-between">
                                    <span>{lbl}</span>
                                    <span className="font-bold">{doc.category_scores?.[k] ?? 0}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Editable report draft */}
                    <div className="lg:col-span-2 bg-white border border-ash-gray-light p-6" data-testid="report-draft-editor">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="font-display font-bold text-xl">Report draft</h2>
                            {msg && <span className="text-sm text-ash-orange font-bold">{msg}</span>}
                        </div>

                        <Editor label="What we noticed" value={draft?.what_we_noticed || ""} onChange={setF("what_we_noticed")} rows={4} testid="edit-what-noticed" />
                        <Editor label="Best starting point" value={draft?.best_starting_point || ""} onChange={setF("best_starting_point")} rows={2} testid="edit-best-start" />
                        <Editor label="How ASH can help" value={draft?.how_ash_can_help || ""} onChange={setF("how_ash_can_help")} rows={3} testid="edit-how-ash" />

                        <h3 className="font-display font-bold mt-6 mb-3">Category notes</h3>
                        {Object.entries(labels).map(([k, lbl]) => (
                            <Editor
                                key={k}
                                label={lbl}
                                value={draft?.category_notes?.[k] || ""}
                                onChange={setNote(k)}
                                rows={2}
                                testid={`edit-note-${k}`}
                            />
                        ))}

                        <h3 className="font-display font-bold mt-6 mb-3">Top recommendations</h3>
                        {(draft?.top_recommendations || []).map((rec, i) => (
                            <Editor
                                key={i}
                                label={`Recommendation ${i + 1}`}
                                value={rec}
                                onChange={setListItem("top_recommendations", i)}
                                rows={2}
                                testid={`edit-rec-${i}`}
                            />
                        ))}

                        <h3 className="font-display font-bold mt-6 mb-3">30-day plan</h3>
                        {(draft?.thirty_day_plan || []).map((wk, i) => (
                            <Editor
                                key={i}
                                label={`Week ${i + 1}`}
                                value={wk}
                                onChange={setListItem("thirty_day_plan", i)}
                                rows={2}
                                testid={`edit-week-${i}`}
                            />
                        ))}

                        <div className="mt-6">
                            <button onClick={save} disabled={saving} className="btn-primary flex items-center gap-2" data-testid="save-draft-btn">
                                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                                Save draft
                            </button>
                        </div>
                    </div>
                </div>
            </section>
        </Layout>
    );
}

const Stat = ({ label, value, accent }) => (
    <div className="bg-white border border-ash-gray-light p-4">
        <div className="text-xs uppercase tracking-widest font-bold text-ash-gray-dark">{label}</div>
        <div className={`mt-2 font-display font-black text-2xl tracking-tight ${accent ? "text-ash-orange" : "text-ash-black"}`}>{value}</div>
    </div>
);

const Editor = ({ label, value, onChange, rows = 3, testid }) => (
    <div className="mb-4">
        <label className="block text-xs uppercase tracking-widest font-bold text-ash-gray-dark mb-1">{label}</label>
        <textarea
            className="field-input"
            rows={rows}
            value={value}
            onChange={onChange}
            data-testid={testid}
        />
    </div>
);


const SCORE_COLOR = (v) => {
    if (v == null) return "text-ash-gray-dark";
    if (v >= 90) return "text-ash-orange";
    if (v >= 50) return "text-ash-black";
    return "text-red-600";
};

const ScoreCell = ({ v }) => (
    <span className={`font-display font-black text-xl ${SCORE_COLOR(v)}`}>
        {v == null ? "—" : v}
        {v != null && <span className="text-xs font-normal text-ash-gray-dark">/100</span>}
    </span>
);

const WebsiteAudit = ({ lighthouse, url }) => {
    if (!lighthouse) {
        return (
            <p className="text-sm text-ash-gray-dark">
                No audit yet for <span className="font-mono">{url}</span>. Click <b>Run audit</b> to start.
            </p>
        );
    }
    if (lighthouse.status === "pending") {
        return (
            <p className="text-sm text-ash-gray-dark flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" /> Audit in progress for{" "}
                <span className="font-mono">{lighthouse.url}</span> — refresh in ~30-60 seconds.
            </p>
        );
    }
    if (lighthouse.status === "failed") {
        return (
            <p className="text-sm text-red-600">
                Audit failed: {lighthouse.mobile?.error || lighthouse.desktop?.error || "unknown error"}
            </p>
        );
    }
    const cats = [
        ["performance", "Performance"],
        ["accessibility", "Accessibility"],
        ["best_practices", "Best Practices"],
        ["seo", "SEO"],
    ];
    return (
        <div data-testid="website-audit-content">
            <p className="text-xs text-ash-gray-dark mb-3">
                Audited <span className="font-mono">{lighthouse.url}</span> · {new Date(lighthouse.ran_at).toLocaleString()}
            </p>
            <div className="overflow-x-auto">
                <table className="text-sm">
                    <thead>
                        <tr className="text-left">
                            <th className="pr-6 pb-2 text-xs uppercase tracking-widest font-bold text-ash-gray-dark">Category</th>
                            <th className="pr-6 pb-2 text-xs uppercase tracking-widest font-bold text-ash-gray-dark">Mobile</th>
                            <th className="pr-6 pb-2 text-xs uppercase tracking-widest font-bold text-ash-gray-dark">Desktop</th>
                        </tr>
                    </thead>
                    <tbody>
                        {cats.map(([k, label]) => (
                            <tr key={k}>
                                <td className="pr-6 py-1 font-medium">{label}</td>
                                <td className="pr-6 py-1"><ScoreCell v={lighthouse.mobile?.[k]} /></td>
                                <td className="pr-6 py-1"><ScoreCell v={lighthouse.desktop?.[k]} /></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {lighthouse.ai_summary && (
                <p className="mt-4 text-sm text-ash-gray-dark"><b className="text-ash-black">What we noticed:</b> {lighthouse.ai_summary}</p>
            )}
            {Array.isArray(lighthouse.ai_recommendations) && lighthouse.ai_recommendations.length > 0 && (
                <ul className="mt-2 text-sm text-ash-gray-dark list-disc pl-5 space-y-1">
                    {lighthouse.ai_recommendations.slice(0, 5).map((r, i) => (
                        <li key={i}>{r}</li>
                    ))}
                </ul>
            )}
        </div>
    );
};
